import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Instagram, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

// TikTok Icon Component
const TikTokIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#leistungen", label: "Training" },
    { href: "#trainer", label: "Über Mich" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="navigation"
      className={`nav-fixed ${
        isScrolled ? "py-3" : "py-4"
      } transition-all duration-300`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a
            href="#"
            data-testid="logo-link"
            className="flex items-center gap-3"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_coach-reserve-2/artifacts/rhhlwg7u_Simple%20Sports%20Football%20%26%20Soccer%20Badge%20Logo%20%282%29.png" 
              alt="Aktas Personal Fussballakademie Logo"
              className="h-12 w-12 object-contain"
            />
            <div className="hidden sm:block">
              <p className="font-['Bebas_Neue'] text-lg text-white leading-tight">
                YASIN AKTAS
              </p>
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA]">
                Personal Fussballtraining
              </p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="nav-link text-sm uppercase tracking-wide"
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
              </button>
            ))}
            <Link
              to="/camps"
              className="nav-link text-sm uppercase tracking-wide"
              data-testid="nav-camps"
            >
              Camps
            </Link>
            <Link
              to="/akademie"
              className="nav-link text-sm uppercase tracking-wide"
              data-testid="nav-akademie"
            >
              Akademie
            </Link>
            <a
              href="https://instagram.com/fussballtraining_aktas"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#A1A1AA] hover:text-[#c1ff72] transition-colors duration-300"
              data-testid="nav-instagram"
            >
              <Instagram size={20} />
            </a>
            <a
              href="https://tiktok.com/@fussballtraining_aktas"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#A1A1AA] hover:text-[#c1ff72] transition-colors duration-300"
              data-testid="nav-tiktok"
            >
              <TikTokIcon size={20} />
            </a>
            <Link to="/buchen">
              <Button
                className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-semibold px-6 py-2 rounded-none transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(193,255,114,0.5)]"
                data-testid="nav-cta-button"
              >
                <Calendar className="w-4 h-4 mr-1" />
                JETZT BUCHEN
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden absolute top-full left-0 right-0 bg-[#0A0A0A] border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col py-4">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="text-left px-6 py-3 text-[#A1A1AA] hover:text-white hover:bg-white/5 transition-colors duration-300"
                >
                  {link.label}
                </button>
              ))}
              <Link
                to="/camps"
                className="text-left px-6 py-3 text-[#A1A1AA] hover:text-white hover:bg-white/5 transition-colors duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Camps
              </Link>
              <Link
                to="/akademie"
                className="text-left px-6 py-3 text-[#A1A1AA] hover:text-white hover:bg-white/5 transition-colors duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Akademie
              </Link>
              <Link
                to="/meine-termine"
                className="text-left px-6 py-3 text-[#A1A1AA] hover:text-white hover:bg-white/5 transition-colors duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-meine-termine"
              >
                Meine Termine
              </Link>
              <div className="px-6 py-4">
                <Link to="/buchen" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button
                    className="w-full bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-semibold py-3 rounded-none"
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    JETZT BUCHEN
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
